// Accessible tooltip primitive. Plan 72.
//
// Renders a single trigger child with a popover that appears on hover or
// keyboard focus (desktop) and on tap of an adjacent (?) icon (mobile, no
// hover). The tooltip body is rendered with `role="tooltip"` and the
// trigger is wired with `aria-describedby` so screen readers announce the
// description after the trigger's own accessible name.
//
// Behaviour:
//   - Desktop: a 200 ms hover delay debounces accidental reveals; mouse-leave
//     and Esc both dismiss.
//   - Keyboard: focusing the trigger with `:focus-visible` reveals the
//     tooltip; blur dismisses.
//   - Mobile: the (?) icon adjacent to the trigger toggles the tooltip on
//     tap. (`onClick`, not long-press, to match iOS Safari conventions.)
//
// Placement is fixed at "below the trigger" — locked decision per plan 72
// (avoids viewport clipping on mobile). Future placements can be added via
// a `placement` prop without breaking call sites.

import {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { HelpCircle } from '../icons';
import { cn } from '../utils/cn';

export interface TooltipProps {
  /**
   * The element the tooltip describes. Must be a single React element so
   * we can attach `aria-describedby`, hover, and focus handlers without
   * an extra wrapper that would change the layout.
   */
  children: ReactElement;
  /** Tooltip body. Plain text or rich nodes both work. */
  content: ReactNode;
  /**
   * Show a small `(?)` icon adjacent to the trigger as the mobile/keyboard
   * tap target. Default `true` — mobile users have no hover, so this is the
   * only way to reach the description.
   */
  showHelpIcon?: boolean;
  /** Accessible label for the (?) icon button. */
  helpIconLabel?: string;
  /** Hover-reveal delay (ms). Default 200. */
  openDelayMs?: number;
  /** Optional className applied to the wrapping span. */
  className?: string;
  /** Optional className applied to the popover body. */
  contentClassName?: string;
}

/**
 * Wraps a single child element with a hover/focus/tap-revealable tooltip.
 *
 * The child receives `aria-describedby` pointing at the tooltip's id and
 * its existing handlers are preserved. We do NOT clone in a focus-trap or
 * portal — the tooltip lives inline so layout stays predictable in the
 * Dashboard My Sessions card.
 */
export const Tooltip = ({
  children,
  content,
  showHelpIcon = true,
  helpIconLabel = 'More info',
  openDelayMs = 200,
  className,
  contentClassName,
}: TooltipProps) => {
  const tooltipId = useId();
  const [open, setOpen] = useState(false);
  const openTimerRef = useRef<number | null>(null);

  const clearOpenTimer = useCallback(() => {
    if (openTimerRef.current != null) {
      window.clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
  }, []);

  const scheduleOpen = useCallback(() => {
    clearOpenTimer();
    openTimerRef.current = window.setTimeout(() => {
      setOpen(true);
      openTimerRef.current = null;
    }, openDelayMs);
  }, [clearOpenTimer, openDelayMs]);

  const closeNow = useCallback(() => {
    clearOpenTimer();
    setOpen(false);
  }, [clearOpenTimer]);

  // Esc dismisses the tooltip globally — matches Modal/Drawer convention so
  // keyboard users have a single dismissal idiom across primitives.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeNow();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, closeNow]);

  // Cleanup any pending open-timer if we unmount mid-hover.
  useEffect(() => () => clearOpenTimer(), [clearOpenTimer]);

  if (!isValidElement(children)) {
    // boundary: development-time guard — Tooltip must wrap a single element.
    throw new Error('<Tooltip> requires a single React element child.');
  }

  // Compose handlers so we don't clobber any the consumer passed in.
  const childProps = (children as ReactElement<Record<string, unknown>>).props ?? {};
  const previousMouseEnter = childProps.onMouseEnter as ((e: ReactMouseEvent) => void) | undefined;
  const previousMouseLeave = childProps.onMouseLeave as ((e: ReactMouseEvent) => void) | undefined;
  const previousFocus = childProps.onFocus as ((e: FocusEvent) => void) | undefined;
  const previousBlur = childProps.onBlur as ((e: FocusEvent) => void) | undefined;
  const previousKeyDown = childProps.onKeyDown as ((e: ReactKeyboardEvent) => void) | undefined;
  const previousDescribedBy = childProps['aria-describedby'] as string | undefined;

  const triggerProps: Record<string, unknown> = {
    'aria-describedby': previousDescribedBy ? `${previousDescribedBy} ${tooltipId}` : tooltipId,
    onMouseEnter: (e: ReactMouseEvent) => {
      previousMouseEnter?.(e);
      scheduleOpen();
    },
    onMouseLeave: (e: ReactMouseEvent) => {
      previousMouseLeave?.(e);
      closeNow();
    },
    onFocus: (e: FocusEvent) => {
      previousFocus?.(e);
      // Focus reveals immediately — no delay; keyboard users expect parity
      // with screen-reader announcement of the described-by node.
      clearOpenTimer();
      setOpen(true);
    },
    onBlur: (e: FocusEvent) => {
      previousBlur?.(e);
      closeNow();
    },
    onKeyDown: (e: ReactKeyboardEvent) => {
      previousKeyDown?.(e);
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeNow();
      }
    },
  };

  const triggerWithTooltip = cloneElement(children, triggerProps);

  const onHelpClick = (e: ReactMouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen((prev) => !prev);
  };

  return (
    <span className={cn('relative inline-flex items-center gap-1.5', className)}>
      {triggerWithTooltip}
      {showHelpIcon && (
        <button
          type="button"
          onClick={onHelpClick}
          aria-label={helpIconLabel}
          aria-expanded={open}
          aria-controls={tooltipId}
          className="inline-flex items-center justify-center w-6 h-6 rounded-full text-stone-400 hover:text-stone-700 hover:bg-stone-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900"
        >
          <HelpCircle className="w-4 h-4" aria-hidden="true" />
        </button>
      )}
      {open && (
        <span
          id={tooltipId}
          role="tooltip"
          className={cn(
            // Below the trigger (locked decision per plan 72). Center horizontally
            // with a soft cap so long copy wraps neatly on small screens.
            'absolute left-1/2 -translate-x-1/2 top-full mt-2 z-50',
            'w-[min(20rem,calc(100vw-2rem))] max-w-sm',
            'bg-stone-900 text-stone-50 text-xs leading-relaxed',
            'rounded-2xl shadow-2xl px-4 py-3',
            contentClassName
          )}
        >
          {content}
        </span>
      )}
    </span>
  );
};
